import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { toast } from 'sonner';
import { EnvelopeSimple, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';

import { getApiBaseUrl } from '../utils/api';
export function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send reset email');
      }
      
      setSubmitted(true);
      toast.success('Check your email for reset instructions');
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
              alt="STAY ON Logo" 
              className="h-16 mx-auto mb-4"
            />
          </div>
          
          <Card className="bg-card border-border">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} weight="fill" className="text-green-500" />
              </div>
              <h2 className="text-xl font-heading text-white mb-2">Check Your Email</h2>
              <p className="text-muted-foreground mb-6">
                If an account exists for <span className="text-primary font-medium">{email}</span>, 
                you'll receive a password reset link shortly.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                The link will expire in 1 hour.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="w-full"
                  data-testid="try-another-email-btn"
                >
                  Try Another Email
                </Button>
                <Link to="/login">
                  <Button variant="ghost" className="w-full">
                    <ArrowLeft size={20} className="mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
            alt="STAY ON Logo" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-heading text-white mb-2">FORGOT PASSWORD</h1>
          <p className="text-muted-foreground">Enter your email to receive a reset link</p>
        </div>
        
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-center text-lg font-heading flex items-center justify-center gap-2">
              <EnvelopeSimple size={24} className="text-primary" />
              Reset Password
            </CardTitle>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                  data-testid="forgot-email-input"
                  placeholder="Enter your email"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
                data-testid="send-reset-btn"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          </CardContent>
          
          <CardFooter className="flex justify-center">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1">
              <ArrowLeft size={16} />
              Back to Login
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
